import Vue from 'vue';
import { eachDayOfInterval, isWithinInterval, addDays } from 'date-fns';
import BaseModel from './base';
import Address from '@/models/list/address';
import StageActivity from '@/models/list/stage-activity';
import { TeamManager } from '../managers/team';
import { rServices } from '@/plugins/api';
import { localeFormatDefault } from '@/plugins/date-print';
import { TEAM_TYPES } from '@/modules/Team/constants';
import { DEFAULT_PARAMS } from '@/utils/objectsRHandler';

export const DEFAULT_NAME_STAGE = 'Основной этап';

// Модель этапа мероприятия
export default class Stage extends BaseModel {
  constructor(stage, url) {
    super(stage, url);

    this.id = stage.id;
    this.event_id = stage.event_id;
    this.nomination_id = stage.nomination_id;
    this.is_team = stage.is_team;
    this.name = stage.name || DEFAULT_NAME_STAGE;
    this.start_at = localeFormatDefault(stage.start_at);
    this.end_at = localeFormatDefault(stage.end_at);
    this.description = stage.description;
    this.options = stage.options;
    this.format = stage.options.format;
    this.addresses = (stage.addresses || []).map((address) => new Address(address));
    this.address_ids = stage.addresses ? stage.addresses.map((i) => i.id) : [];
    this.parent_id = stage.parent_id;
    this.price = stage.price;
    this.qualifying = stage.qualifying;
    this.specification = stage.specification;
    this.activities = stage.activities || [];
    this.activitiesByDate = [];

    this.parent_template = stage.parent_template || null;

    this.poll_journals = stage.poll_journals || [];
    this.score = stage.score || null;

    this.memberActivities = [];
  }

  async setupMyTeams() {
    const myTeams = await this.getMyTeams();
    this.teams = myTeams;
  }

  /**
   * Получить список моих команд
   */
  getMyTeams({ params } = DEFAULT_PARAMS) {
    return TeamManager[TEAM_TYPES.stage].myTeams({
      params: {
        ...params,
        'search[event_stage_id]': this.id,
      },
    });
  }

  async getActivities(params = {}) {
    const extendParams = {
      sort: '-id',
      expand: 'documents,images,experts,addresses,telegram,videos,is_favorite',
      ...params,
    };
    const args = [this.id];

    const { getStageActivities, getPublicStageActivities } = rServices.stages;
    const isPublic = this.url.includes('public');
    const method = isPublic ? getPublicStageActivities : getStageActivities;

    const { response, error } = await method({ args, params: extendParams });
    if (!error) {
      this.activities = (response?.data?.data || []).map((activity) => new StageActivity(activity));
      this.activitiesByDate = this.#getActivitiesByDate();
    }

    return {
      activities: this.activities,
      activitiesByDate: this.activitiesByDate,
    };
  }

  #getActivitiesByDate() {
    // { day: Date (fns), list: activity[] }[];
    const totalDays = eachDayOfInterval({
      start: new Date(this.start_at),
      end: new Date(this.end_at),
    });
    // { day: Date, list: activity[] }[];
    const activitiesList = [];

    this.activities.forEach((activity) => {
      const activityDays = totalDays.filter((date) =>
        isWithinInterval(new Date(date), {
          start: addDays(new Date(activity.start_at), -1),
          end: new Date(activity.end_at),
        }),
      );

      activityDays.forEach((date) => {
        const listItem = activitiesList.find((item) => item.day === date);
        if (listItem) {
          listItem.list.push(activity);
        } else {
          activitiesList.push({
            day: date,
            list: [activity],
          });
        }
      });
    });

    activitiesList.sort((a, b) => new Date(a.day) - new Date(b.day));
    return activitiesList.map(({ day, list }) => ({
      day,
      list: list.sort((a, b) => new Date(a.start_at) - new Date(b.start_at)),
    }));
  }

  // members activity

  async getMemberActivities(params) {
    const extendParams = {
      expand: 'videos,telegram',
      'search[my_activity]': 1,
      'search[event_stage_id]': this.id,
      ...params,
    };
    const { response } = await rServices.stages.getMembersActivities({ params: extendParams });
    this.memberActivities = (response?.data?.data || []).map((activity) => new StageActivity(activity));
    return this.memberActivities;
  }

  async joinMemberActivity(id) {
    const { response, error } = await rServices.stages.membersActivityJoin({ args: [id] });
    if (!error) {
      const activity = this.activities.find((item) => item.id === id);
      if (!activity) await this.getMemberActivities();
      else this.memberActivities = [...this.memberActivities, activity];
      Vue.$toast.success('Вы записались на активность');
    }
    return response;
  }

  async leaveMemberActivity(id) {
    const { response, error } = await rServices.stages.membersActivityLeave({ args: [id] });
    if (!error) {
      this.memberActivities = this.memberActivities.filter((activity) => activity.id !== id);
      Vue.$toast.success('Вы отписались от активности');
    }
    return response;
  }
}
